import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Order, OrderStatus } from '../types/order'
import { faker } from '@faker-js/faker'

interface OrderStore {
  orders: Order[]
  currentOrder: Order | null

  // Actions
  addOrder: (order: Omit<Order, 'id' | 'order_number' | 'created_at' | 'updated_at'>) => Order
  updateOrderStatus: (orderId: string, status: OrderStatus, note?: string) => void
  setCurrentOrder: (order: Order | null) => void
  getOrderByNumber: (orderNumber: string) => Order | undefined
  clearOrders: () => void
}

const generateOrderNumber = (): string => {
  const prefix = 'BT'
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = faker.string.alphanumeric(4).toUpperCase()
  return `${prefix}${timestamp}${random}`
}

export const useOrderStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      orders: [],
      currentOrder: null,

      addOrder: (orderData) => {
        const now = new Date().toISOString()
        const newOrder: Order = {
          ...orderData,
          id: faker.string.uuid(),
          order_number: generateOrderNumber(),
          status: 'pending',
          timeline: [
            {
              status: 'pending',
              timestamp: now,
              note: 'Order placed successfully',
            },
          ],
          created_at: now,
          updated_at: now,
        }

        set((state) => ({
          orders: [newOrder, ...state.orders],
          currentOrder: newOrder,
        }))

        return newOrder
      },

      updateOrderStatus: (orderId, status, note) => {
        const now = new Date().toISOString()

        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  status,
                  timeline: [
                    ...order.timeline,
                    {
                      status,
                      timestamp: now,
                      note: note || `Order status updated to ${status}`,
                    },
                  ],
                  updated_at: now,
                }
              : order
          ),
        }))
      },

      setCurrentOrder: (order) => set({ currentOrder: order }),

      getOrderByNumber: (orderNumber) => {
        return get().orders.find((o) => o.order_number === orderNumber)
      },

      clearOrders: () => set({ orders: [], currentOrder: null }),
    }),
    {
      name: 'BotTola-orders',
    }
  )
)
